/**
 * API 客户端实现
 *
 * 增强：401 自动清理缓存、网络错误友好消息、5xx 自动重试
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios'
import type { ApiClientConfig, ApiResponse } from './types'

let _instance: AxiosInstance | null = null

/**
 * 判断本次失败请求是否带有 Bearer（请求拦截器在 getToken 有值时才会附加）。
 * OAuth 回跳后并发请求可能尚未带上 token 却打到了需登录的接口，若此时 401 仍 clearAuthStorage，
 * 会清掉同轮换票刚写入的 auth_token，表现为「同意授权后又回授权页」循环。
 */
function requestHadAuthorizationHeader(config: AxiosRequestConfig | undefined): boolean {
  if (!config?.headers) return false
  const h = config.headers as Record<string, unknown> & { get?: (k: string) => string | undefined }
  if (typeof h.get === 'function') {
    return Boolean(
      h.get('Authorization')?.toString()
      || h.get('authorization')?.toString(),
    )
  }
  return Boolean(h.Authorization || h.authorization)
}

/** 与 @cn-kis/feishu-sdk 的 safeGetItem 一致：Edge 等环境可能禁用 localStorage，token 落在 sessionStorage */
function readAuthTokenFromBrowserStorage(): string | null {
  try {
    const t = localStorage.getItem('auth_token')
    if (t) return t
  } catch {
    /* ignore */
  }
  try {
    return sessionStorage.getItem('auth_token')
  } catch {
    return null
  }
}

/** 401 时清理两端存储，避免仅清 localStorage 导致下次仍从 sessionStorage 读到旧 token */
function clearAuthStorage() {
  const keys = [
    'auth_token',
    'auth_user',
    'auth_profile',
    'auth_profile_token',
    'auth_roles',
    'auth_workbenches',
    'auth_token_ts',
    'auth_session_meta',
  ]
  for (const k of keys) {
    try {
      localStorage.removeItem(k)
    } catch {
      /* ignore */
    }
    try {
      sessionStorage.removeItem(k)
    } catch {
      /* ignore */
    }
  }
  /** 与 @cn-kis/feishu-sdk useFeishuAuth 同步：同页 axios 清 storage 后须立刻清 React token 态，否则会误判已登录且不再带 Authorization → 401 风暴 */
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new Event('cnkis-auth-token-cleared'))
    } catch {
      /* ignore */
    }
  }
}

async function retryRequest(instance: AxiosInstance, config: AxiosRequestConfig): Promise<any> {
  await new Promise((r) => setTimeout(r, 1000))
  return instance.request(config)
}

/** Django DEBUG 等场景下 5xx 可能返回 HTML，不可当作用户可见文案 */
function isLikelyHtmlErrorPayload(text: string): boolean {
  const t = text.trimStart()
  if (!t) return false
  if (t.startsWith('<!')) return true
  if (/^<html[\s>]/i.test(t)) return true
  if (/<title>\s*Server Error/i.test(t)) return true
  return false
}

/**
 * 后端未启动时，Vite 等对 /api 的代理常返回 502/503/504 且无 JSON msg，易被误判为泛化「服务不可用」。
 */
function friendlyUpstreamErrorMessage(status: number): string {
  if (status === 502 || status === 503 || status === 504) {
    return (
      '无法连接后端（网关返回 '
      + `${status}）。请先在本机启动 Django：打开 backend/本机启动后端.bat，或执行 `
      + 'python manage.py runserver 0.0.0.0:8001（设备台 vite 会把 /api 代理到 8001）。'
    )
  }
  if (status === 500) {
    return (
      '后端返回 500（接口未捕获异常）。请查看运行 runserver 的终端中的完整 Python 堆栈以定位根因，'
      + '并对照浏览器网络面板中失败请求的 URL 定位接口；本提示为泛化说明，与当前页面功能无必然对应'
      + '（勿仅凭此文判断为设备台账导入问题）。'
    )
  }
  return '服务暂时不可用，请稍后重试'
}

/**
 * 创建/配置 API 客户端实例
 */
export function createApiClient(config: ApiClientConfig = {}): AxiosInstance {
  const {
    baseURL: rawBaseURL = '/api/v1',
    timeout = 30000,
    getToken = () => readAuthTokenFromBrowserStorage(),
    onUnauthorized,
    skipClearAuthStorageOnAuthError = false,
  } = config
  const baseURL = rawBaseURL

  const instance = axios.create({
    baseURL,
    timeout,
    headers: { 'Content-Type': 'application/json' },
  })

  instance.interceptors.request.use(
    (reqConfig) => {
      const path = String(reqConfig.url || '')
      /** 知情签署测试 H5：公开口令接口勿带工作台 JWT，避免无效/过期 Token 触发 401 导致提交无明确 JSON 提示 */
      const isConsentTestPublic =
        path.includes('/protocol/public/consent-test-queue')
        || path.includes('/protocol/public/consent-test-queue-meta')
        || path.includes('/protocol/public/consent-test-submit')
        || path.includes('/protocol/public/consent-test-receipt')
        || path.includes('/protocol/public/consent-test-icf-preview-pdf')
        || path.includes('/protocol/public/consent-test-icf-preview-sidecar')
        || path.includes('/protocol/public/consent-test-scan')
        || path.includes('/protocol/public/consent-test-face')
      const isQuestionnairePlaytestPublic = path.includes('/protocol/public/questionnaire-playtest')
      const token = getToken()
      if (token && !isConsentTestPublic && !isQuestionnairePlaytestPublic) {
        reqConfig.headers.Authorization = `Bearer ${token}`
      }
      // FormData 上传时移除 Content-Type，让浏览器自动设置 multipart boundary
      if (reqConfig.data instanceof FormData && reqConfig.headers) {
        const h = reqConfig.headers as { delete?: (key: string) => void } & Record<string, unknown>
        if (typeof h.delete === 'function') {
          h.delete('Content-Type')
          h.delete('content-type')
        } else {
          delete h['Content-Type']
          delete h['content-type']
        }
      }
      return reqConfig
    },
    (error) => Promise.reject(error),
  )

  instance.interceptors.response.use(
    (response) => {
      // 二进制响应（如文件下载）不检查 code
      if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
        return response
      }
      const body = response.data as { code?: number | string; msg?: string; data?: unknown } | undefined
      // 兼容 code 为字符串 "200" 或数字 200（严格相等曾误判为业务失败；且部分代理/中间层会改类型）
      if (body && typeof body === 'object' && !Array.isArray(body) && body.code != null) {
        const c = Number(body.code)
        if (!Number.isNaN(c) && c !== 0 && c !== 200) {
          const msg = body.msg || '请求失败'
          const err = new Error(msg) as Error & { response: typeof response }
          err.response = response
          return Promise.reject(err)
        }
      }
      return response
    },
    async (error: AxiosError) => {
      if (error.response) {
        const { status } = error.response
        const rawBody = error.response.data
        let backendMsg: string | null = null
        let requiredPermission: string | null = null
        let requiredPermissions: string[] = []
        /** responseType: blob 时，4xx/5xx 的 JSON 错误体在 Blob 内，需解析后才能展示后端 msg（5xx 不解析则易误报「服务暂时不可用」） */
        if (rawBody instanceof Blob && status >= 400 && rawBody.size > 0 && rawBody.size < 65536) {
          try {
            const text = await rawBody.text()
            if (text.trimStart().startsWith('{')) {
              const j = JSON.parse(text) as { msg?: string; message?: string }
              backendMsg = (typeof j.msg === 'string' && j.msg.trim() ? j.msg : null) ||
                (typeof j.message === 'string' && j.message.trim() ? j.message : null)
            }
          } catch {
            /* ignore */
          }
        }
        const body = rawBody instanceof Blob ? null : rawBody
        if (!backendMsg && body && typeof body === 'object' && !Array.isArray(body)) {
          const b = body as { msg?: string; message?: string; detail?: string | unknown; data?: unknown }
          backendMsg = b.msg || b.message || (typeof b.detail === 'string' ? b.detail : null) || null
          if (!backendMsg && Array.isArray(b.detail) && b.detail.length > 0) {
            const msgs = (b.detail as unknown[])
              .map((item) => {
                if (item && typeof item === 'object' && 'msg' in item) {
                  const m = (item as { msg?: string }).msg
                  return typeof m === 'string' && m.trim() ? m : ''
                }
                if (typeof item === 'string' && item.trim()) return item
                return ''
              })
              .filter(Boolean)
            if (msgs.length) backendMsg = msgs.join('；')
          }
          const payload = b.data
          if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
            const p = payload as { required_permission?: unknown; required_permissions?: unknown }
            if (typeof p.required_permission === 'string' && p.required_permission.trim()) {
              requiredPermission = p.required_permission.trim()
            }
            if (Array.isArray(p.required_permissions)) {
              requiredPermissions = p.required_permissions
                .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
                .map((item) => item.trim())
            }
          }
        }
        if (!backendMsg && body && typeof body === 'string') {
          if (body.length < 500) {
            if (!isLikelyHtmlErrorPayload(body)) {
              backendMsg = body
            }
          } else if (/<!DOCTYPE|Traceback|<title>/i.test(body)) {
            const m = body.match(/<title>([^<]+)<\/title>/i)
            backendMsg = m?.[1]?.trim()
              ? `服务器错误（${m[1].trim().slice(0, 120)}）`
              : '服务器内部错误，请稍后重试或联系管理员'
          }
        }

        const authRequired =
          status === 401
          || (status === 403 && (
            backendMsg === '请先登录'
            || (
              body
              && typeof body === 'object'
              && !Array.isArray(body)
              && typeof (body as { data?: unknown }).data === 'object'
              && (body as { data?: { error_code?: string } }).data?.error_code === 'AUTH_REQUIRED'
            )
          ))

        if (authRequired) {
          if (!skipClearAuthStorageOnAuthError && requestHadAuthorizationHeader(error.config)) {
            clearAuthStorage()
          }
          onUnauthorized?.()
          const err = new Error(backendMsg || '未授权，请重新登录') as Error & { response: typeof error.response }
          err.response = error.response
          return Promise.reject(err)
        }

        // 4xx: 使用后端返回的 msg
        if (status >= 400 && status < 500) {
          let msg =
            backendMsg
            || (status === 422 ? '请求参数无法通过校验（422），请检查表单字段或查询参数' : null)
            || (status === 404 ? '请求的资源不存在' : `请求失败 (${status})`)
          if (status === 403) {
            const permTips = [
              requiredPermission,
              ...requiredPermissions,
            ].filter((item): item is string => !!item)
            if (permTips.length > 0) {
              msg = `${backendMsg || '无权限访问'}（缺少权限：${permTips.join(' / ')}）`
            } else if (!backendMsg) {
              msg = '无权限访问（请联系管理员分配对应权限）'
            }
          }
          const err = new Error(msg) as Error & { response: typeof error.response }
          err.response = error.response
          return Promise.reject(err)
        }

        // 5xx: retry once
        const config = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined
        if (status >= 500 && !config?._retried && config) {
          config._retried = true
          try {
            return await retryRequest(instance, config)
          } catch {
            const err = new Error(backendMsg || friendlyUpstreamErrorMessage(status)) as Error & {
              response: typeof error.response
            }
            err.response = error.response
            return Promise.reject(err)
          }
        }
        if (status >= 500) {
          const err = new Error(backendMsg || friendlyUpstreamErrorMessage(status)) as Error & {
            response: typeof error.response
          }
          err.response = error.response
          return Promise.reject(err)
        }
      } else {
        // 无 response：断网、CORS、或客户端/代理超时
        const ax = error as AxiosError & Error & { code?: string }
        const lower = (ax?.message || '').toLowerCase()
        const isTimeout =
          ax?.code === 'ECONNABORTED' || lower.includes('timeout') || lower.includes('exceeded')
        const isBrowserNetwork =
          ax.message === 'Network Error' || ax.code === 'ERR_NETWORK'
        let message: string
        if (isTimeout) {
          message =
            '请求超时（导入大文件或服务器处理较慢时可能发生）。请稍后重试，或联系管理员调大网关/代理超时。'
        } else if (isBrowserNetwork) {
          message =
            '无法连接 API 服务器。请确认后端已在 http://localhost:8001 运行'
            + '（设备台开发时 vite 将 /api 代理到该端口；勿单独只开前端）。'
        } else {
          message = '网络连接失败，请检查网络后重试'
        }
        const networkErr = new Error(message) as Error & { originalError: unknown }
        networkErr.originalError = error
        return Promise.reject(networkErr)
      }
      return Promise.reject(error)
    },
  )

  _instance = instance
  return instance
}

function getInstance(): AxiosInstance {
  if (!_instance) {
    _instance = createApiClient()
  }
  return _instance
}

/** 获取 axios 实例，用于 blob 下载等需要完整 response 的场景 */
export function getAxiosInstance(): AxiosInstance {
  return getInstance()
}

/**
 * API 请求方法封装
 */
export const api = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    getInstance().get(url, config).then((res) => res.data),

  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    getInstance().post(url, data, config).then((res) => res.data),

  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    getInstance().put(url, data, config).then((res) => res.data),

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    getInstance().delete(url, config).then((res) => res.data),

  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    getInstance().patch(url, data, config).then((res) => res.data),

  /** 勿手动设 multipart Content-Type（缺 boundary 会导致服务端无法解析，axios 报「网络连接失败」） */
  upload: <T = unknown>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    getInstance().post(url, formData, config).then((res) => res.data),
}
